import React from "react";
import { phoneNumbers } from "../Static";
import {
  FiPhone,
  FiPhoneCall,
  FiMail,
  FiClock,
  FiMapPin,
  FiHeadphones,
  FiArrowRight,
  FiExternalLink,
  FiShield,
  FiNavigation,
} from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";
import LottieAnimator from "./ui-elements/LottieAnimator";
import helpdeskBanner from "../assets/animation/helpdesk-banner.json";

export default function ContactsSection() {
  const { t, language } = useLanguage();

  const telNumbers = phoneNumbers.filter((p) => p.mode === "tel");
  const emailAddresses = phoneNumbers.filter((p) => p.mode === "mailto");

  const getOperatorMeta = (num) => {
    if (num.startsWith("060")) {
      return {
        name: language === "en" ? "Landline / Real Time" : language === "ru" ? "Городская Сеть / Real Time" : "Քաղաքային Ցանց / Real Time",
        tagClass: "bg-red-50 text-brandRed border-red-200/80",
        dotColor: "bg-brandRed",
        isPrimary: true,
      };
    }
    if (num.startsWith("091")) {
      return {
        name: "Team Telecom",
        tagClass: "bg-sky-50 text-sky-800 border-sky-200/80",
        dotColor: "bg-sky-500",
        isPrimary: false,
      };
    }
    if (num.startsWith("094")) {
      return {
        name: "Viva-MTS",
        tagClass: "bg-rose-50 text-rose-800 border-rose-200/80",
        dotColor: "bg-rose-500",
        isPrimary: false,
      };
    }
    return {
      name: "Ucom",
      tagClass: "bg-emerald-50 text-emerald-800 border-emerald-200/80",
      dotColor: "bg-emerald-500",
      isPrimary: false,
    };
  };

  return (
    <section id="contacts" className="w-full py-24 bg-gradient-to-b from-slate-50 via-slate-100/50 to-slate-50 border-t border-slate-200/80 relative">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Section Heading */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-red-50 border border-red-100 text-brandRed text-xs font-bold uppercase tracking-wider mb-3 shadow-sm">
            <FiHeadphones size={13} />
            <span>{t("contacts.badge")}</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight uppercase">
            {t("contacts.title")}
          </h2>
          <p className="text-slate-600 text-sm md:text-base mt-3 leading-relaxed">
            {t("contacts.subtitle")}
          </p>
        </div>

        {/* Unified Hotline Highlight Banner */}
        <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white rounded-3xl p-6 md:p-8 lg:p-10 shadow-2xl mb-12 border border-slate-800/90 relative overflow-hidden">
          <div className="pointer-events-none absolute -right-24 -top-24 w-80 h-80 bg-red-600/15 rounded-full blur-3xl"></div>
          <div className="pointer-events-none absolute -left-24 -bottom-24 w-80 h-80 bg-slate-700/20 rounded-full blur-3xl"></div>

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6 md:gap-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-5">
              <div className="w-16 h-16 rounded-2xl bg-brandRed/20 text-brandRed border border-brandRed/40 flex items-center justify-center flex-shrink-0 shadow-glow-red">
                <FiPhoneCall size={30} className="animate-pulse" />
              </div>
              <div>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2.5 mb-1.5">
                  <span className="inline-flex items-center space-x-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-wider">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-ping"></span>
                    <span>{t("contacts.operatorsOnline")}</span>
                  </span>
                  <span className="text-xs text-slate-400">
                    • {t("contacts.callCenterDuty")}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-black uppercase text-white tracking-tight">
                  {t("contacts.supportBannerTitle")}
                </h3>
                <p className="text-slate-400 text-xs md:text-sm mt-1 flex items-center justify-center sm:justify-start gap-1.5">
                  <FiClock size={13} className="text-red-400" />
                  <span>{t("contacts.supportBannerHours")}</span>
                </p>
              </div>
            </div>

            <a
              href="tel:+37460666999"
              className="inline-flex items-center justify-center space-x-3 bg-brandRed hover:bg-brandRedHover text-white px-8 py-4 rounded-2xl font-bebas text-2xl tracking-widest shadow-glow-red hover:shadow-glow-red-lg transition-all duration-200 transform hover:-translate-y-0.5 flex-shrink-0"
            >
              <FiPhone size={22} />
              <span>060 666 999</span>
            </a>
          </div>
        </div>

        {/* 2-Column Balanced Master Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Direct Phone Lines & Email Channels (7 Cols) */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* Clean Phone Lines Directory */}
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/90 shadow-xl">
              <div className="flex items-center space-x-3 border-b border-slate-100 pb-4 mb-6">
                <div className="w-11 h-11 rounded-2xl bg-red-50 text-brandRed flex items-center justify-center border border-red-100/90 flex-shrink-0">
                  <FiPhone size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black uppercase text-slate-900 tracking-tight">
                    {t("contacts.mobilePhonesTitle")}
                  </h3>
                  <p className="text-slate-400 text-xs">
                    {language === "en" ? "Direct lines for all mobile carriers in Armenia" : language === "ru" ? "Прямые линии всех мобильных операторов Армении" : "Ուղիղ կապ Հայաստանի բոլոր բջջային ցանցերով"}
                  </p>
                </div>
              </div>

              {/* Clean, Elegant Stacked Phone Items */}
              <div className="space-y-3">
                {telNumbers.map((item, idx) => {
                  const meta = getOperatorMeta(item.number);

                  return (
                    <a
                      key={idx}
                      href={`tel:${item.lnumber}`}
                      className="group flex items-center justify-between p-4 rounded-2xl border border-slate-100 hover:border-red-200 bg-slate-50/60 hover:bg-red-50/30 transition-all duration-200 shadow-sm"
                    >
                      <div className="flex items-center space-x-3.5">
                        <span className={`w-2.5 h-2.5 rounded-full ${meta.dotColor} flex-shrink-0`}></span>
                        <div>
                          <div className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border mb-0.5 ${meta.tagClass}`}>
                            {meta.name}
                          </div>
                          <div className="text-2xl font-bold font-bebas tracking-wider text-slate-900 group-hover:text-brandRed transition-colors">
                            {item.number}
                          </div>
                        </div>
                      </div>

                      <div className="w-10 h-10 rounded-2xl bg-white border border-slate-200/80 group-hover:bg-brandRed group-hover:border-brandRed text-slate-400 group-hover:text-white flex items-center justify-center transition-all shadow-sm">
                        <FiPhone size={16} />
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Clean Email Directory */}
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/90 shadow-xl">
              <div className="flex items-center space-x-3 border-b border-slate-100 pb-4 mb-6">
                <div className="w-11 h-11 rounded-2xl bg-red-50 text-brandRed flex items-center justify-center border border-red-100/90 flex-shrink-0">
                  <FiMail size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black uppercase text-slate-900 tracking-tight">
                    {t("contacts.emailTitle")}
                  </h3>
                  <p className="text-slate-400 text-xs">
                    {language === "en" ? "Official email communication channels" : language === "ru" ? "Официальные каналы электронной почты" : "Պաշտոնական հաղորդակցություն և գործընկերություն"}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                {emailAddresses.map((item, idx) => (
                  <a
                    key={idx}
                    href={`mailto:${item.lnumber}`}
                    className="group flex items-center justify-between p-4 rounded-2xl border border-slate-100 hover:border-red-200 bg-slate-50/60 hover:bg-red-50/30 transition-all duration-200 shadow-sm"
                  >
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                        {item.number.includes("hr")
                          ? t("contacts.emailHrTitle")
                          : t("contacts.emailGeneralTitle")}
                      </div>
                      <div className="text-base font-bold text-slate-900 group-hover:text-brandRed transition-colors break-all">
                        {item.number}
                      </div>
                    </div>

                    <div className="w-10 h-10 rounded-2xl bg-white border border-slate-200/80 group-hover:bg-brandRed group-hover:border-brandRed text-slate-400 group-hover:text-white flex items-center justify-center transition-all shadow-sm flex-shrink-0 ml-3">
                      <FiMail size={16} />
                    </div>
                  </a>
                ))}
              </div>

              {/* SLA Response Guarantee Note */}
              <div className="p-4 rounded-2xl bg-amber-50/70 border border-amber-200/70 flex items-start space-x-3.5">
                <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FiShield size={16} />
                </div>
                <div>
                  <div className="font-bold uppercase tracking-wider text-xs text-amber-900">
                    {t("contacts.quickReply")}
                  </div>
                  <p className="text-slate-600 text-xs mt-0.5 leading-relaxed">
                    {t("contacts.quickReplyDesc")}
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Headquarters & Interactive Office Center with Lottie (5 Cols) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/90 shadow-xl overflow-hidden relative">
              
              {/* Lottie Animation Illustration Frame */}
              <div className="w-full bg-slate-50/80 rounded-2xl border border-slate-100 p-2 mb-6 flex items-center justify-center relative overflow-hidden">
                <div className="w-full max-w-[280px] h-[190px] flex items-center justify-center">
                  <LottieAnimator lottie={helpdeskBanner} />
                </div>
              </div>

              {/* Office Details */}
              <div className="border-b border-slate-100 pb-4 mb-5">
                <div className="inline-flex items-center space-x-2 text-xs font-bold text-brandRed uppercase tracking-wider mb-1">
                  <FiMapPin size={13} />
                  <span>{t("contacts.officeTitle")}</span>
                </div>
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                  {language === "en" ? "Real Time Telecom Headquarters" : language === "ru" ? "Главный Офис «Реал Тайм»" : "«Ռեալ Թայմ» Գլխավոր Գրասենյակ"}
                </h3>
              </div>

              <div className="space-y-4">
                {/* Address Item */}
                <div className="p-4 rounded-2xl bg-slate-50/70 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                    <FiMapPin size={12} className="text-brandRed" />
                    <span>{t("contacts.addressLabel")}</span>
                  </div>
                  <div className="text-sm font-bold text-slate-900 leading-snug">
                    {t("contacts.addressValue")}
                  </div>
                </div>

                {/* Coverage Zone Item */}
                <div className="p-4 rounded-2xl bg-slate-50/70 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                    <FiNavigation size={12} className="text-sky-600" />
                    <span>{t("contacts.coverageZoneLabel")}</span>
                  </div>
                  <div className="text-xs text-slate-600 leading-relaxed font-medium">
                    {t("contacts.coverageZoneValue")}
                  </div>
                </div>

                {/* Working Hours Item */}
                <div className="p-4 rounded-2xl bg-slate-50/70 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                    <FiClock size={12} className="text-amber-600" />
                    <span>{t("contacts.officeHoursLabel")}</span>
                  </div>
                  <div className="text-xs font-bold text-slate-800">
                    {t("contacts.officeHoursValue")}
                  </div>
                </div>
              </div>

              {/* Interactive Navigation Actions */}
              <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                <a
                  href="#coverage"
                  className="flex-1 inline-flex items-center justify-center space-x-2 py-3 px-4 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all shadow-md group"
                >
                  <span>{t("contacts.viewCoverageMap")}</span>
                  <FiArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>

                <a
                  href="https://yandex.ru/maps/?text=ՀՀ+ք.+Վաղարշապատ+Շահումյան+39"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center space-x-2 py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors"
                >
                  <FiExternalLink size={14} />
                  <span>{t("contacts.openInMaps")}</span>
                </a>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
